import { tg } from './telegram';

/** Measured app space, not an IME animation clock. Never store editor contents. */
export function keyboardViewport() {
  const visual = window.visualViewport;
  const top = visual?.scale === 1 ? visual.offsetTop : 0;
  const height = Math.min(visual?.scale === 1 ? visual.height : innerHeight, tg()?.viewportHeight || innerHeight);
  return { top, height, bottom: top + height };
}

export function isKeyboardEditor(element: Element | null): element is HTMLElement {
  return element instanceof HTMLElement && (element.isContentEditable ||
    element instanceof HTMLTextAreaElement && !element.readOnly && !element.disabled ||
    element instanceof HTMLInputElement && !element.readOnly && !element.disabled &&
    ['text', 'search', 'email', 'url', 'tel', 'password', 'number'].includes(element.type));
}

/** Reveal only the necessary delta, through existing scroll owners. Called on
 * focus/geometry, never on scroll: the user retains manual scroll ownership. */
export function revealKeyboardEditor(element: HTMLElement, bottom = keyboardViewport().bottom - 12) {
  const viewport = keyboardViewport();
  let top = viewport.top + 12;
  const panel = element.closest<HTMLElement>('.sheet-interactive');
  if (panel) {
    const bounds = panel.getBoundingClientRect();
    top = Math.max(top, bounds.top + 8);
    const footer = panel.querySelector<HTMLElement>('.calorie-foot, .nf');
    bottom = Math.min(bottom, footer?.getBoundingClientRect().top ?? bounds.bottom - 8);
  }
  const delta = () => {
    const rect = element.getBoundingClientRect();
    // A selected combobox option is part of the editor, not a second window
    // scroll owner. Keep its input visible as well as the active suggestion.
    const id = element.getAttribute('aria-activedescendant');
    const option = id ? document.getElementById(id) : null;
    const end = option ? Math.min(option.getBoundingClientRect().bottom, rect.bottom + 100) : rect.bottom;
    return rect.top < top ? rect.top - top : end > bottom ? Math.min(end - bottom, rect.top - top) : 0;
  };
  for (let owner = element.parentElement; owner; owner = owner.parentElement) {
    if (owner === document.body || owner === document.documentElement) break;
    const style = getComputedStyle(owner);
    if (/(auto|scroll)/.test(style.overflowY) && owner.scrollHeight > owner.clientHeight) owner.scrollTop += delta();
  }
  // Modal/persistent panels own their own scrollers, never the document below.
  if (!panel && document.body.style.overflow !== 'hidden') window.scrollBy({ top: delta(), behavior: 'instant' });
}
